type QuestionnaireConfig = {
  id: string;
  domain: "client" | "newborn";
  title: string;
  displayName: string;
  isQuickButton?: boolean;
};

const questionnaires: QuestionnaireConfig[] = [
  // Client questionnaires
  {
    id: "client_ipp_vitals",
    domain: "client",
    title: "IPP Vitals",
    displayName: "IPP Vitals",
    isQuickButton: true,
  },
  {
    id: "client_placenta",
    domain: "client",
    title: "Placenta",
    displayName: "Placenta",
    isQuickButton: true,
  },
  {
    id: "client_position",
    domain: "client",
    title: "Position",
    displayName: "Position",
    isQuickButton: true,
  },
  {
    id: "client_cord_cut_clamped",
    domain: "client",
    title: "Cord Cut/Clamped",
    displayName: "Cord Cut/Clamped",
    isQuickButton: true,
  },
  {
    id: "client_first_latch",
    domain: "client",
    title: "First Latch",
    displayName: "First Latch",
    isQuickButton: true,
  },
  {
    id: "client_assessment_note",
    domain: "client",
    title: "Note",
    displayName: "Client Assessment Note",
    isQuickButton: true,
  },
  {
    id: "client_initial_ipp_assessment",
    domain: "client",
    title: "Initial IPP Assessment",
    displayName: "Initial IPP Assessment",
  },
  {
    id: "client_full_ipp_assessment",
    domain: "client",
    title: "Full IPP Assessment",
    displayName: "Full IPP Assessment",
  },
  {
    id: "client_meds_therapies",
    domain: "client",
    title: "Meds & Therapies",
    displayName: "Meds & Therapies",
  },
  {
    id: "client_input_output",
    domain: "client",
    title: "Input/Output",
    displayName: "Input/Output",
  },
  {
    id: "client_perineum",
    domain: "client",
    title: "Perineum",
    displayName: "Perineum",
  },
  // SOS
  {
    id: "postpartumHemorrhage",
    domain: "client",
    title: "Postpartum Hemorrhage",
    displayName: "Postpartum Hemorrhage",
  },
  {
    id: "resuscitation",
    domain: "client",
    title: "Resuscitation",
    displayName: "Resuscitation",
  },
  {
    id: "estimatedBloodLoss",
    domain: "client",
    title: "Estimated Blood Loss",
    displayName: "Estimated Blood Loss",
  },

  // Newborn questionnaires
  {
    id: "newborn_vitals",
    domain: "newborn",
    title: "Newborn Vitals",
    displayName: "Newborn Vitals",
    isQuickButton: true,
  },
  {
    id: "newborn_cord_cut_clamped",
    domain: "newborn",
    title: "Cord Cut/Clamped",
    displayName: "Cord Cut/Clamped",
    isQuickButton: true,
  },
  {
    id: "newborn_first_latch",
    domain: "newborn",
    title: "First Latch",
    displayName: "First Latch",
    isQuickButton: true,
  },
  {
    id: "newborn_apgar",
    domain: "newborn",
    title: "APGAR",
    displayName: "APGAR",
    isQuickButton: true,
  },
  {
    id: "newborn_assessment_note",
    domain: "newborn",
    title: "Note",
    displayName: "Assessment Note",
    isQuickButton: true,
  },
  {
    id: "newborn_initial_assessment",
    domain: "newborn",
    title: "Initial Newborn Assessment",
    displayName: "Initial Newborn Assessment",
  },
  {
    id: "newborn_full_assessment",
    domain: "newborn",
    title: "Full Newborn Assessment",
    displayName: "Full Newborn Assessment",
  },
  {
    id: "newborn_physical_exam",
    domain: "newborn",
    title: "Newborn Physical Exam",
    displayName: "Newborn Physical Exam",
  },
  {
    id: "newborn_feeding",
    domain: "newborn",
    title: "Feeding",
    displayName: "Feeding",
  },
  {
    id: "newborn_meds_therapies",
    domain: "newborn",
    title: "Meds & Therapies",
    displayName: "Meds & Therapies",
  },
  {
    id: "newborn_location",
    domain: "newborn",
    title: "Newborn Location",
    displayName: "Newborn Location",
  },
  // SOS
  {
    id: "shoulderDystocia",
    domain: "newborn",
    title: "Shoulder Dystocia",
    displayName: "Shoulder Dystocia",
  },
];

// Derive enums from configuration
export enum PostpartumClientQuestionnaireType {
  ClientIppVitals = "client_ipp_vitals",
  ClientPlacenta = "client_placenta",
  ClientPosition = "client_position",
  ClientCordCutClamped = "client_cord_cut_clamped",
  ClientFirstLatch = "client_first_latch",
  ClientAssessmentNote = "client_assessment_note",
  ClientInitialIppAssessment = "client_initial_ipp_assessment",
  ClientFullIppAssessment = "client_full_ipp_assessment",
  ClientMedsTherapies = "client_meds_therapies",
  ClientInputOutput = "client_input_output",
  ClientPerineum = "client_perineum",
  ClientBirthing = "birthing",
  // SOS
  ClientPostpartumHemorrhage = "postpartumHemorrhage",
  ClientResuscitation = "resuscitation",
}

export enum PostpartumNewbornQuestionnaireType {
  NewbornVitals = "newborn_vitals",
  NewbornCordCutClamped = "newborn_cord_cut_clamped",
  NewbornFirstLatch = "newborn_first_latch",
  NewbornApgar = "newborn_apgar",
  NewbornNote = "newborn_assessment_note",
  NewbornInitialAssessment = "newborn_initial_assessment",
  NewbornFullAssessment = "newborn_full_assessment",
  NewbornPhysicalExam = "newborn_physical_exam",
  NewbornFeeding = "newborn_feeding",
  NewbornMedsTherapies = "newborn_meds_therapies",
  NewbornLocation = "newborn_location",
  NewbornBirthing = "birthing",
  // SOS
  NewbornShoulderDystocia = "shoulderDystocia",
}

/**
 * Postpartum newborn questionnaires charted on the newborn once one is linked. Birthing and
 * Shoulder Dystocia chart the delivery, so they stay on the mother; they also share their names
 * with the labor flowsheet's own forms.
 */
export const NEWBORN_SUBJECT_QUESTIONNAIRE_TYPES: ReadonlySet<string> = new Set([
  PostpartumNewbornQuestionnaireType.NewbornVitals,
  PostpartumNewbornQuestionnaireType.NewbornCordCutClamped,
  PostpartumNewbornQuestionnaireType.NewbornFirstLatch,
  PostpartumNewbornQuestionnaireType.NewbornApgar,
  PostpartumNewbornQuestionnaireType.NewbornNote,
  PostpartumNewbornQuestionnaireType.NewbornInitialAssessment,
  PostpartumNewbornQuestionnaireType.NewbornFullAssessment,
  PostpartumNewbornQuestionnaireType.NewbornPhysicalExam,
  PostpartumNewbornQuestionnaireType.NewbornFeeding,
  PostpartumNewbornQuestionnaireType.NewbornMedsTherapies,
  PostpartumNewbornQuestionnaireType.NewbornLocation,
]);

export enum PostpartumSOSQuestionnaireType {
  Resuscitation = "resuscitation",
  EstimatedBloodLoss = "estimatedBloodLoss",
  ShoulderDystocia = "shoulderDystocia",
  PostpartumHemorrhage = "postpartumHemorrhage",
}

// Create a Set for O(1) lookups of SOS questionnaire types
const postpartumSOSQuestionnaireTypeSet = new Set(Object.values(PostpartumSOSQuestionnaireType));

// Derive arrays of questionnaire types
export const POSTPARTUM_CLIENT_QUESTIONNAIRE_TYPES = Object.values(
  PostpartumClientQuestionnaireType,
);
export const POSTPARTUM_NEWBORN_QUESTIONNAIRE_TYPES = Object.values(
  PostpartumNewbornQuestionnaireType,
);

// Helper function to create questionnaire maps and buttons
function createQuestionnaireConfigs<
  T extends PostpartumClientQuestionnaireType | PostpartumNewbornQuestionnaireType,
>(
  domain: "client" | "newborn",
  isQuickButton?: boolean,
):
  | Record<T, string>
  | Array<{
      title: string;
      questionnaireName: T;
      displayTitle: string;
    }> {
  const filteredQuestionnaires = questionnaires.filter(
    (q) =>
      q.domain === domain &&
      (isQuickButton === undefined
        ? true
        : isQuickButton
          ? q.isQuickButton === true
          : !q.isQuickButton) &&
      !postpartumSOSQuestionnaireTypeSet.has(q.id as PostpartumSOSQuestionnaireType),
  );

  // For display names, we need to handle both cases
  if (isQuickButton === undefined) {
    return filteredQuestionnaires.reduce(
      (acc, q) => ({ ...acc, [q.id]: q.displayName }),
      {} as Record<T, string>,
    );
  }

  // For buttons, we return the mapped array
  return filteredQuestionnaires.map((q) => ({
    title: q.title,
    questionnaireName: q.id as T,
    displayTitle: q.displayName,
  }));
}

// Create display name maps
export const POSTPARTUM_CLIENT_QUESTIONNAIRE_DISPLAY_NAMES =
  createQuestionnaireConfigs<PostpartumClientQuestionnaireType>("client") as Record<
    PostpartumClientQuestionnaireType,
    string
  >;
export const POSTPARTUM_NEWBORN_QUESTIONNAIRE_DISPLAY_NAMES =
  createQuestionnaireConfigs<PostpartumNewbornQuestionnaireType>("newborn") as Record<
    PostpartumNewbornQuestionnaireType,
    string
  >;

// Create quick buttons
export const CLIENT_POSTPARTUM_FLOWSHEET_QUICK_BUTTONS =
  createQuestionnaireConfigs<PostpartumClientQuestionnaireType>("client", true) as Array<{
    title: string;
    questionnaireName: PostpartumClientQuestionnaireType;
    displayTitle: string;
  }>;
export const NEWBORN_POSTPARTUM_FLOWSHEET_QUICK_BUTTONS =
  createQuestionnaireConfigs<PostpartumNewbornQuestionnaireType>("newborn", true) as Array<{
    title: string;
    questionnaireName: PostpartumNewbornQuestionnaireType;
    displayTitle: string;
  }>;

// Create menu buttons
export const CLIENT_POSTPARTUM_FLOWSHEET_MENU_BUTTONS =
  createQuestionnaireConfigs<PostpartumClientQuestionnaireType>("client", false) as Array<{
    title: string;
    questionnaireName: PostpartumClientQuestionnaireType;
    displayTitle: string;
  }>;
export const NEWBORN_POSTPARTUM_FLOWSHEET_MENU_BUTTONS =
  createQuestionnaireConfigs<PostpartumNewbornQuestionnaireType>("newborn", false) as Array<{
    title: string;
    questionnaireName: PostpartumNewbornQuestionnaireType;
    displayTitle: string;
  }>;

// Export types
export type ClientPostpartumFlowsheetQuickButton =
  (typeof CLIENT_POSTPARTUM_FLOWSHEET_QUICK_BUTTONS)[number];
export type NewbornPostpartumFlowsheetQuickButton =
  (typeof NEWBORN_POSTPARTUM_FLOWSHEET_QUICK_BUTTONS)[number];
export type PostpartumFlowsheetQuickButton =
  | ClientPostpartumFlowsheetQuickButton
  | NewbornPostpartumFlowsheetQuickButton;
export type PostpartumFlowsheetMenuButton =
  | (typeof CLIENT_POSTPARTUM_FLOWSHEET_MENU_BUTTONS)[number]
  | (typeof NEWBORN_POSTPARTUM_FLOWSHEET_MENU_BUTTONS)[number];

export enum ClientPostpartumFlowsheetForms {
  ClientIppVitals = "forms/clientPostpartumFlowSheet/client_ipp_vitals",
  ClientPlacenta = "forms/clientPostpartumFlowSheet/client_placenta",
  ClientPosition = "forms/clientPostpartumFlowSheet/client_position",
  ClientCordCutClamped = "forms/clientPostpartumFlowSheet/client_cord_cut_clamped",
  ClientFirstLatch = "forms/clientPostpartumFlowSheet/client_first_latch",
  ClientAssessmentNote = "forms/clientPostpartumFlowSheet/client_assessment_note",
  ClientInitialIppAssessment = "forms/clientPostpartumFlowSheet/client_initial_ipp_assessment",
  ClientFullIppAssessment = "forms/clientPostpartumFlowSheet/client_full_ipp_assessment",
  ClientMedsTherapies = "forms/clientPostpartumFlowSheet/client_meds_therapies",
  ClientInputOutput = "forms/clientPostpartumFlowSheet/client_input_output",
  ClientPerineum = "forms/clientPostpartumFlowSheet/client_perineum",
  ClientResuscitation = "forms/laborFlowSheet/resuscitation",
  ClientBirthing = "forms/laborFlowSheet/birthing",
  ClientPostpartumHemorrhage = "forms/laborFlowSheet/postpartumHemorrhage",
  ClientEstimatedBloodLoss = "forms/laborFlowSheet/estimatedBloodLoss",
  ClientAdmissionDischarge = "forms/laborFlowSheet/admissionDischarge",
}

type ClientFullIppAssessmentSection = {
  id: PostpartumClientQuestionnaireType;
  label: string;
  formPath: string;
};

export const CLIENT_FULL_IPP_ASSESSMENT_SECTIONS: ClientFullIppAssessmentSection[] = [
  {
    id: PostpartumClientQuestionnaireType.ClientIppVitals,
    label:
      POSTPARTUM_CLIENT_QUESTIONNAIRE_DISPLAY_NAMES[
        PostpartumClientQuestionnaireType.ClientIppVitals
      ],
    formPath: ClientPostpartumFlowsheetForms.ClientIppVitals,
  },
  {
    id: PostpartumClientQuestionnaireType.ClientPosition,
    label:
      POSTPARTUM_CLIENT_QUESTIONNAIRE_DISPLAY_NAMES[
        PostpartumClientQuestionnaireType.ClientPosition
      ],
    formPath: ClientPostpartumFlowsheetForms.ClientPosition,
  },
  {
    id: PostpartumClientQuestionnaireType.ClientPerineum,
    label:
      POSTPARTUM_CLIENT_QUESTIONNAIRE_DISPLAY_NAMES[
        PostpartumClientQuestionnaireType.ClientPerineum
      ],
    formPath: ClientPostpartumFlowsheetForms.ClientPerineum,
  },
  {
    id: PostpartumClientQuestionnaireType.ClientInputOutput,
    label:
      POSTPARTUM_CLIENT_QUESTIONNAIRE_DISPLAY_NAMES[
        PostpartumClientQuestionnaireType.ClientInputOutput
      ],
    formPath: ClientPostpartumFlowsheetForms.ClientInputOutput,
  },
  {
    id: PostpartumClientQuestionnaireType.ClientMedsTherapies,
    label:
      POSTPARTUM_CLIENT_QUESTIONNAIRE_DISPLAY_NAMES[
        PostpartumClientQuestionnaireType.ClientMedsTherapies
      ],
    formPath: ClientPostpartumFlowsheetForms.ClientMedsTherapies,
  },
];

export enum NewbornPostpartumFlowsheetForms {
  NewbornVitals = "forms/newbornPostpartumFlowSheet/newborn_vitals",
  NewbornCordCutClamped = "forms/newbornPostpartumFlowSheet/newborn_cord_cut_clamped",
  NewbornFirstLatch = "forms/newbornPostpartumFlowSheet/newborn_first_latch",
  NewbornApgar = "forms/newbornPostpartumFlowSheet/newborn_apgar",
  NewbornAssessmentNote = "forms/newbornPostpartumFlowSheet/newborn_assessment_note",
  NewbornInitialAssessment = "forms/newbornPostpartumFlowSheet/newborn_initial_assessment",
  NewbornFullAssessment = "forms/newbornPostpartumFlowSheet/newborn_full_assessment",
  NewbornPhysicalExam = "forms/newbornPostpartumFlowSheet/newborn_physical_exam",
  NewbornFeeding = "forms/newbornPostpartumFlowSheet/newborn_feeding",
  NewbornMedsTherapies = "forms/newbornPostpartumFlowSheet/newborn_meds_and_therapies",
  NewbornLocation = "forms/newbornPostpartumFlowSheet/newborn_location",
  NewbornBirthing = "forms/laborFlowSheet/birthing",
  NewbornShoulderDystocia = "forms/laborFlowSheet/shoulderDystocia",
  NewbornAdmissionDischarge = "forms/laborFlowSheet/admissionDischarge",
}

export type NewbornFullIppAssessmentSection = {
  id: PostpartumNewbornQuestionnaireType;
  label: string;
  formPath: string;
};

export const NEWBORN_FULL_IPP_ASSESSMENT_SECTIONS: NewbornFullIppAssessmentSection[] = [
  {
    id: PostpartumNewbornQuestionnaireType.NewbornVitals,
    label:
      POSTPARTUM_NEWBORN_QUESTIONNAIRE_DISPLAY_NAMES[
        PostpartumNewbornQuestionnaireType.NewbornVitals
      ],
    formPath: NewbornPostpartumFlowsheetForms.NewbornVitals,
  },
  {
    id: PostpartumNewbornQuestionnaireType.NewbornFeeding,
    label:
      POSTPARTUM_NEWBORN_QUESTIONNAIRE_DISPLAY_NAMES[
        PostpartumNewbornQuestionnaireType.NewbornFeeding
      ],
    formPath: NewbornPostpartumFlowsheetForms.NewbornFeeding,
  },
  {
    id: PostpartumNewbornQuestionnaireType.NewbornMedsTherapies,
    label:
      POSTPARTUM_NEWBORN_QUESTIONNAIRE_DISPLAY_NAMES[
        PostpartumNewbornQuestionnaireType.NewbornMedsTherapies
      ],
    formPath: NewbornPostpartumFlowsheetForms.NewbornMedsTherapies,
  },
  {
    id: PostpartumNewbornQuestionnaireType.NewbornLocation,
    label:
      POSTPARTUM_NEWBORN_QUESTIONNAIRE_DISPLAY_NAMES[
        PostpartumNewbornQuestionnaireType.NewbornLocation
      ],
    formPath: NewbornPostpartumFlowsheetForms.NewbornLocation,
  },
  {
    id: PostpartumNewbornQuestionnaireType.NewbornNote,
    label:
      POSTPARTUM_NEWBORN_QUESTIONNAIRE_DISPLAY_NAMES[
        PostpartumNewbornQuestionnaireType.NewbornNote
      ],
    formPath: NewbornPostpartumFlowsheetForms.NewbornAssessmentNote,
  },
];

export enum CommonComponent {
  Notes = "notes",
  Signature = "signature",
}

export enum ClientIPPVitalsComponent {
  Systolic = "8480-6",
  Diastolic = "8462-4",
  Pulse = "8867-4",
  Temperature = "8310-5",
  TemperatureValue = "ippVitalsTemperatureValue",
  TemperatureUnits = "ippVitalsTemperatureUnits",
  TemperatureUnit = "client-ipp-vitals-temperature-unit",
  TemperatureMethod = "client-ipp-vitals-temperature-method",
  TemperatureMethodOther = "client-ipp-vitals-temperature-method-other",
  FundusMeasurement = "client-ipp-vitals-fundus-measurement",
  FundusValue = "client-ipp-vitals-fundus-value",
  FundalTexture = "client-ipp-vitals-fundal-texture",
  Note = "client-ipp-vitals-note",
  Signature = "client-ipp-vitals-signature",
}

export enum ClientPlacentaComponent {
  DeliveryMethod = "client-placenta-delivery-method",
  Presentation = "client-placenta-presentation",
  PresentationOther = "client-placenta-presentation-other",
  ThreeVesselCord = "client-placenta-three-vessel-cord",
  Intact = "client-placenta-intact",
  Appearance = "client-placenta-appearance",
  AppearanceOther = "client-placenta-appearance-other",
}

export enum ClientPositionComponent {
  Position = "client-position",
  InWater = "client-in-water",
  InWaterOther = "client-in-water-other",
}

export enum ClientMedsTherapiesComponent {
  IVAccess = "client-meds-therapies-iv-access",
  FluidVolume = "client-meds-therapies-fluid-volume",
  Medication = "client-meds-therapies-medication",
  Form = "client-meds-therapies-form",
  Dosage = "client-meds-therapies-dosage",
  Frequency = "client-meds-therapies-frequency",
  PRN = "client-meds-therapies-prn",
  SpecialInstructions = "client-meds-therapies-special-instructions",
  Therapy = "client-meds-therapies-therapy",
  Reason = "client-meds-therapies-reason",
  Note = "client-meds-therapies-note",
  Signature = "client-meds-therapies-signature",
}

export enum NewbornMedsTherapiesComponent {
  IVAccess = "newborn-meds-and-therapies-iv-access",
  Medication = "newborn-meds-and-therapies-medication-1",
  Form = "newborn-meds-and-therapies-form-1",
  Duration = "newborn-meds-and-therapies-duration-1",
  DurationXDays = "newborn-meds-and-therapies-duration-x-days-1",
  Dosage = "newborn-meds-and-therapies-dosage-1",
  Therapy = "newborn-meds-and-therapies-therapy-1",
  Reason = "newborn-meds-and-therapies-reason-1",
}

export enum ClientInputOutputComponent {
  Input = "client-io-input",
  Output = "client-io-output",
}

export enum ClientPerineumComponent {
  Status = "client-perineum-status",
  OtherLaceration = "client-perineum-other-laceration",
  Repair = "client-perineum-repair",
}

export enum ClientCordCutClampedComponent {
  Cut = "client-cord-cut-clamped-cut",
  Clamped = "client-cord-cut-clamped-clamped",
  Note = "client-cord-cut-clamped-note",
}

export enum ClientFirstLatchComponent {
  Nursing = "client-first-latch-nursing",
  Formula = "client-first-latch-formula",
  Note = "client-first-latch-note",
}

export enum ClientInitialIppAssessmentComponent {
  Alert = "client-initial-ipp-assessment-alert",
  Management = "client-initial-ipp-assessment-management",
  Bleeding = "client-initial-ipp-assessment-bleeding",
  Note = "client-initial-ipp-assessment-note",
}

export enum ClientAssessmentNoteComponent {
  Note = "client-assessment-note",
}

export enum NewbornFirstLatchComponent {
  Nursing = "newborn-first-latch-nursing",
  Formula = "newborn-first-latch-formula",
  Note = "newborn-first-latch-note",
}

export enum NewbornAssessmentNoteComponent {
  Note = "newborn-assessment-note",
}

export enum NewbornCordCutClampedComponent {
  Cut = "newborn-cord-cut-clamped-cut",
  Clamped = "newborn-cord-cut-clamped-clamped",
  Note = "newborn-cord-cut-clamped-note",
}

export enum NewbornVitalsComponent {
  HeartRate = "newborn-vitals-heart-rate",
  RespiratoryRate = "newborn-vitals-respiratory-rate",
  Temperature = "newborn-vitals-temperature-value",
  TemperatureUnit = "newborn-vitals-temperature-unit",
  TemperatureMethod = "newborn-vitals-temperature-method",
  TemperatureMethodOther = "newborn-vitals-temperature-method-other",
  Color = "newborn-vitals-color",
  Void = "newborn-vitals-void",
  Stool = "newborn-vitals-stool",
  StoolNotes = "newborn-vitals-stool-notes",
  VoidNotes = "newborn-vitals-void-notes",
}

export enum NewbornApgarComponent {
  Type = "newborn-apgar-type",
  TypeOther = "newborn-apgar-type-other",
  HeartRate = "newborn-apgar-heart-rate",
  RespiratoryEffort = "newborn-apgar-respiratory-effort",
  ReflexStimuli = "newborn-apgar-reflex-stimuli",
  MuscleTone = "newborn-apgar-muscle-tone",
  Color = "newborn-apgar-color",
  Notes = "newborn-apgar-notes",
}

// Common values used across forms
export const FORM_OPTIONS = {
  OTHER: "Other",
  NOT_NORMAL: "Not Normal",
  Fahrenheit: "[degF]",
} as const;

// Create separate maps for client and newborn questionnaires
export const CLIENT_POSTPARTUM_FLOW_SHEET_QUESTIONNAIRE_MAP = Object.fromEntries([
  ...questionnaires.filter((q) => q.domain === "client").map((q) => [q.displayName, q.id]),
  // Add special case questionnaires
  ["Resuscitation", "forms/laborFlowSheet/resuscitation"],
  ["Birthing", "forms/laborFlowSheet/birthing"],
  ["Postpartum Hemorrhage", "forms/laborFlowSheet/postpartumHemorrhage"],
  ["Estimated Blood Loss", "forms/laborFlowSheet/estimatedBloodLoss"],
]);

export const NEWBORN_POSTPARTUM_FLOW_SHEET_QUESTIONNAIRE_MAP = Object.fromEntries([
  ...questionnaires.filter((q) => q.domain === "newborn").map((q) => [q.displayName, q.id]),
  // Add special case questionnaires
  ["Shoulder Dystocia", "forms/laborFlowSheet/shoulderDystocia"],
  ["Birthing", "forms/laborFlowSheet/birthing"],
]);

// Keep the combined map for backward compatibility
export const POSTPARTUM_FLOW_SHEET_QUESTIONNAIRE_MAP = Object.fromEntries(
  questionnaires.map((q) => [q.displayName, q.id]),
);
